/**
 * @fileoverview Pantalla 1 — Inicio.
 * Permite al participante ingresar su código de token o escanear su QR.
 */

import { ScanLine, ShieldCheck } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";

import { AppShell } from "../components/AppShell";
import { LoadingSpinner } from "../components/LoadingSpinner";
import { QRScanner } from "../components/QRScanner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { useParticipacion } from "../context/ParticipacionContext";
import { validarToken } from "../lib/api";
import { esCodigoValido, formatearCodigo, mensajeDeError, mensajeError } from "../lib/utils-app";

export function Inicio() {
    const [codigoInput, setCodigoInput] = useState("");
    const [error, setError] = useState(/** @type {string | null} */ (null));
    const [validando, setValidando] = useState(false);
    const [mostrandoQR, setMostrandoQR] = useState(false);
    const [autoValidando, setAutoValidando] = useState(false);
    const inputRef = useRef(/** @type {HTMLInputElement | null} */ (null));
    const navigate = useNavigate();
    const location = useLocation();
    const [searchParams] = useSearchParams();
    const { dispatch } = useParticipacion();

    // Foco automático al cargar
    useEffect(() => {
        inputRef.current?.focus();
    }, []);

    // Si llegamos a "/" por una redirección con un mensaje (p.ej. token
    // expirado tras pasar validación), lo mostramos como error inicial.
    useEffect(() => {
        const msg = /** @type {any} */ (location.state)?.errorInicial;
        if (typeof msg === "string" && msg) {
            setError(msg);
            // Limpiamos el state para que un refresh no vuelva a mostrarlo.
            navigate(location.pathname, { replace: true, state: null });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Si llega ?codigo=XXX-NNNN del QR vía URL, autovalida
    useEffect(() => {
        const fromUrl = searchParams.get("codigo");
        if (fromUrl) {
            const formatted = formatearCodigo(fromUrl);
            setCodigoInput(formatted);
            if (esCodigoValido(formatted)) {
                setAutoValidando(true);
                handleValidar(formatted);
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    /**
     * @param {string} raw
     */
    function handleChange(raw) {
        setError(null);
        setCodigoInput(formatearCodigo(raw));
    }

    /**
     * @param {string} codigo
     */
    async function handleValidar(codigo) {
        if (!esCodigoValido(codigo)) {
            setError("Formato inválido. Usa el formato XXX-NNNN, por ejemplo IZT-4837.");
            return;
        }
        setValidando(true);
        setError(null);
        try {
            const res = await validarToken(codigo);
            if (!res.valid) {
                // Preferimos el mensaje contextual del backend si llegó (p.ej.
                // "Este código expiró el ..."). Si no, mapeamos por el code.
                setError(res.mensaje || mensajeError(res.error));
                setValidando(false);
                setAutoValidando(false);
                return;
            }
            // valid=true: guardamos código + datos del foro en contexto
            dispatch({ type: "SET_CODIGO", payload: codigo });
            if (res.forum_id && res.alcaldia_foro) {
                dispatch({
                    type: "SET_FORUM",
                    payload: { forum_id: res.forum_id, alcaldia_foro: res.alcaldia_foro },
                });
            }
            if (res.puede_reanudar) {
                navigate("/reanudar");
            } else {
                navigate("/datos-demograficos");
            }
        } catch (e) {
            setError(mensajeDeError(e));
            setValidando(false);
            setAutoValidando(false);
        }
    }

    /**
     * @param {string} codigoQR
     */
    function handleQRLeido(codigoQR) {
        setMostrandoQR(false);
        setCodigoInput(codigoQR);
        handleValidar(codigoQR);
    }

    return (
        <AppShell testid="page-inicio">
            <div className="space-y-6">
                <div className="space-y-2">
                    <h1 className="font-serif-headline text-3xl sm:text-4xl font-semibold leading-tight text-foreground">
                        Rumbo a la Nueva Ley de Participación Ciudadana
                    </h1>
                    <p className="text-base text-muted-foreground">
                        Para participar, ingresa el código que recibiste o escanea el QR de tu papelito.
                    </p>
                </div>

                <form
                    onSubmit={(e) => {
                        e.preventDefault();
                        handleValidar(codigoInput);
                    }}
                    className="space-y-4"
                    noValidate
                >
                    <div className="space-y-1.5">
                        <Label htmlFor="codigo-input" className="text-sm font-medium">
                            Tu código
                        </Label>
                        <Input
                            id="codigo-input"
                            data-testid="codigo-input"
                            ref={inputRef}
                            value={codigoInput}
                            onChange={(e) => handleChange(e.target.value)}
                            placeholder="IZT-4837"
                            inputMode="text"
                            autoCapitalize="characters"
                            autoComplete="off"
                            spellCheck={false}
                            maxLength={8}
                            aria-invalid={!!error}
                            aria-describedby={error ? "codigo-error" : "codigo-hint"}
                            className="text-center font-mono tracking-[0.3em] text-xl h-14 uppercase"
                            disabled={validando || autoValidando}
                        />
                        <p id="codigo-hint" className="text-xs text-muted-foreground">
                            Tres letras, guion y cuatro números.
                        </p>
                    </div>

                    {error ? (
                        <p
                            id="codigo-error"
                            role="alert"
                            className="text-sm text-destructive bg-destructive/10 rounded-lg p-3"
                            data-testid="codigo-error"
                        >
                            {error}
                        </p>
                    ) : null}

                    <Button
                        type="submit"
                        size="lg"
                        className="w-full h-12 text-base font-semibold"
                        disabled={validando || autoValidando}
                        data-testid="continuar-btn"
                    >
                        {validando || autoValidando ? (
                            <>
                                <LoadingSpinner size={18} className="mr-2" /> Verificando...
                            </>
                        ) : (
                            "Continuar"
                        )}
                    </Button>

                    <button
                        type="button"
                        onClick={() => setMostrandoQR(true)}
                        disabled={validando || autoValidando}
                        className="w-full flex items-center justify-center gap-2 py-3 text-sm font-medium text-primary hover:text-primary/80 disabled:opacity-50"
                        data-testid="qr-btn"
                    >
                        <ScanLine size={18} />
                        Escanear QR
                    </button>
                </form>

                <div className="border-t border-border pt-4">
                    <p className="flex items-start gap-2 text-xs text-muted-foreground leading-relaxed">
                        <ShieldCheck size={14} className="mt-0.5 flex-shrink-0 text-primary" />
                        <span>
                            Tu participación es <strong>anónima</strong>. No guardamos tu nombre,
                            teléfono ni correo. Solo se registran tus respuestas para el análisis
                            de la consulta.
                        </span>
                    </p>
                </div>
            </div>

            {mostrandoQR ? (
                <QRScanner
                    onCodigoLeido={handleQRLeido}
                    onCerrar={() => setMostrandoQR(false)}
                />
            ) : null}
        </AppShell>
    );
}
